import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import React from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { colorPool } from "@/module/service-configs-module/statistics-visualization-module/first-svetofor/util";
import { PlayerTimingsDurationPointDto } from "@/shared/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

type Totals = Record<string, number>;

export default function TimingsActionChart({
  data,
}: {
  data: PlayerTimingsDurationPointDto[];
}) {
  const { t } = useTranslation();
  const totalCounts: Totals = {};
  const dataByDuration = new Map<number, Totals>();

  const sortedData = data.sort((el1, el2) => el1.duration - el2.duration);
  console.log("data", data);

  sortedData.forEach((item) => {
    const { duration, heroActionsCount, opponentActionsCount, subOpponentsActionsCount } =
      item;

    // Aggregate totals
    totalCounts.heroActions = (totalCounts.heroActions || 0) + heroActionsCount;
    totalCounts.opponentActions = (totalCounts.opponentActions || 0) + opponentActionsCount;

    subOpponentsActionsCount.forEach((count, index) => {
      const key = `subopponent_${index}`;
      totalCounts[key] = (totalCounts[key] || 0) + count;
    });

    // Aggregate by duration
    const current = dataByDuration.get(duration) || {};
    current.heroActions = (current.heroActions || 0) + heroActionsCount;
    current.opponentActions = (current.opponentActions || 0) + opponentActionsCount;

    subOpponentsActionsCount.forEach((count, index) => {
      const key = `subopponent_${index}`;
      current[key] = (current[key] || 0) + count;
    });

    dataByDuration.set(duration, current);
  });

  // Build data for chart
  const dataForChart: Record<string, number[]> = {};

  for (const [, actorCounts] of dataByDuration.entries()) {
    Object.entries(actorCounts).forEach(([actor, value]) => {
      if (!dataForChart[actor]) {
        dataForChart[actor] = [];
      }
      dataForChart[actor].push(value);
    });
  }

  const datasets = Object.entries(dataForChart).map(([actor, values], index) => {
    const total = totalCounts[actor] || 1; // avoid division by 0
    return {
      label: actor.charAt(0).toUpperCase() + actor.slice(1),
      data: values.map((v) => (v * 100) / total),
      backgroundColor: colorPool[index % colorPool.length],
    };
  });

  return (
    <Bar
      data={{
        labels: Array.from(dataByDuration.keys()).map((d) => d),
        datasets,
      }}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          title: { display: true, text: "Timings Action%" },
          tooltip: {
            callbacks: {
              title: () => {
                return "";
              },
              label: (data: any) => {
                const label = data.label;
                const datasetLabel = data.dataset.label;
                const categoryKey =
                  datasetLabel.charAt(0).toLowerCase() + datasetLabel.slice(1);
                return [
                  `${data.formattedValue}%`,
                  `${t("count")} - ${dataByDuration.get(Number(label))?.[categoryKey] || 0}`,
                  `${t("tableCache.duration")} - ${label}`,
                  `${t("category")} - ${data.dataset.label.replace("Actions", "")}`,
                ];
              },
            },
          },
        },
        scales: {
          y: {
            ticks: {
              callback: (value) => `${value}%`,
            },
          },
        },
      }}
    />
  );
}
